/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getCurrentStorageUserScopeId } from '~/app/storageScope';
import { type DebugSpawnSource,startDebug } from '~/game/actions';
import { GameOverview, GameType, TournamentOverview } from '~/server/src/dtos';

import { createFreeFailed, joinFreeFailed, joinTournamentFailed } from './actions';
import { clearPersistedDebugSpawnSource } from './debugSpawnStorage';

type RestoreState = 'none' | 'loading' | 'error' | 'success';

type RejoinState =
  | {
      type: 'none' | 'loading' | 'error';
    }
  | { type: 'success'; mode: 'training' | 'free' | 'tournament' };

export type SetupErrorKind = 'joinFree' | 'joinTournament' | 'createFree';

export interface State {
  gameId?: string;
  gameName: string;
  gameType?: GameType;
  gameStartDate?: Date;
  games?: GameOverview[];
  tournaments?: TournamentOverview[];
  playerId: number;
  horseAmount: number;

  /** True while free setup poller is waiting for a GETGAMES response. */
  isRefreshingFreeGames: boolean;

  /** True while tournament setup poller is waiting for a GETTOURNAMENTS response. */
  isRefreshingTournaments: boolean;

  // flag to differentiate between new setup game and a reload of the page that should send a restore event
  isNewGame: boolean;

  // if restoring this is indicating the state of the restore, so that if it is failing game screen
  // could e.g. navigate back to the home screen or show an error message
  restoreState: RestoreState;
  rejoinState: RejoinState;

  /** When join/create fails (e.g. email not verified), set here so UI can show message. Cleared on start* or leave. */
  setupError: { kind: SetupErrorKind; error: string } | null;

  /** Set when startDebug runs; used to scope admin snapshot premium bypass in free mode. */
  debugSpawnSource: DebugSpawnSource | null;
}

export const initialState: State = {
  gameName: '',
  playerId: -1,
  horseAmount: 0,
  isRefreshingFreeGames: false,
  isRefreshingTournaments: false,
  isNewGame: false,
  restoreState: 'none',
  rejoinState: { type: 'none' },
  setupError: null,
  debugSpawnSource: null,
};

const slice = createSlice({
  name: 'setup',
  initialState,
  reducers: {
    setGameId: (state: State, action: PayloadAction<string>) => {
      state.gameId = action.payload;
    },
    setGameName: (state: State, action: PayloadAction<string>) => {
      state.gameName = action.payload;
    },
    setGameType: (state: State, action: PayloadAction<GameType | undefined>) => {
      state.gameType = action.payload;
    },
    setGameStartDate: (state: State, action: PayloadAction<Date>) => {
      state.gameStartDate = action.payload;
    },
    setGames: (state: State, action: PayloadAction<GameOverview[]>) => {
      state.games = action.payload;
    },
    setTournaments: (state: State, action: PayloadAction<TournamentOverview[]>) => {
      state.tournaments = action.payload;
    },
    setPlayerId: (state: State, action: PayloadAction<number>) => {
      state.playerId = action.payload;
    },
    setHorseAmount: (state: State, action: PayloadAction<number>) => {
      state.horseAmount = action.payload;
    },
    setIsRefreshingFreeGames: (state: State, action: PayloadAction<boolean>) => {
      state.isRefreshingFreeGames = action.payload;
    },
    setIsRefreshingTournaments: (state: State, action: PayloadAction<boolean>) => {
      state.isRefreshingTournaments = action.payload;
    },
    setRestoreState: (state: State, action: PayloadAction<RestoreState>) => {
      state.restoreState = action.payload;
    },
    setRejoinState: (state: State, action: PayloadAction<RejoinState>) => {
      state.rejoinState = action.payload;
    },
    /** Clears join/create error UI when starting a new attempt or after successful recovery. */
    clearSetupError: (state: State) => {
      state.setupError = null;
    },
    setDebugSpawnSource: (state: State, action: PayloadAction<DebugSpawnSource | null>) => {
      state.debugSpawnSource = action.payload;
    },
  },
  extraReducers: (builder) => {
    const clearDebugSpawnPersistence = (): void => {
      clearPersistedDebugSpawnSource(getCurrentStorageUserScopeId());
    };

    builder
      .addCase(
        'startTraining',
        (_state: State, _action): State => {
          clearDebugSpawnPersistence();
          return { ...initialState, isNewGame: true };
        },
      )
      .addCase(
        'startFree',
        (_state: State, _action): State => {
          clearDebugSpawnPersistence();
          return { ...initialState, isNewGame: true };
        },
      )
      .addCase(
        'startTournament',
        (_state: State, _action): State => {
          clearDebugSpawnPersistence();
          return { ...initialState, isNewGame: true };
        },
      )
      .addCase(startDebug, (_state: State, action): State => ({
        ...initialState,
        isNewGame: true,
        debugSpawnSource: action.payload?.spawnSource ?? null,
      }))
      .addCase(joinFreeFailed, (state: State, action: PayloadAction<{ error: string }>) => {
        state.setupError = { kind: 'joinFree', error: action.payload.error };
      })
      .addCase(joinTournamentFailed, (state: State, action: PayloadAction<{ error: string }>) => {
        state.setupError = { kind: 'joinTournament', error: action.payload.error };
      })
      .addCase(createFreeFailed, (state: State, action: PayloadAction<{ error: string }>) => {
        state.setupError = { kind: 'createFree', error: action.payload.error };
      });
  },
});

const setupActions = slice.actions;
const reducer = slice.reducer;
const name = slice.name;

export { setupActions as actions, name, reducer };
export default slice;
